import { Check } from "lucide-react";
import { cn } from "~/utils/cn";

interface StepIndicatorProps {
	steps: readonly string[];
	/** Zero-based index of the step currently on screen. */
	currentStep: number;
}

/**
 * A compact numbered rail showing wizard progress. The connector between two
 * markers fills once the earlier step is complete.
 */
export const StepIndicator = ({ steps, currentStep }: StepIndicatorProps) => (
	<ol className="flex items-center gap-2">
		{steps.map((label, index) => {
			const isComplete = index < currentStep;
			const isCurrent = index === currentStep;

			return (
				<li
					className={cn("flex items-center gap-2", index > 0 && "flex-1")}
					key={label}
				>
					{index > 0 && (
						<span
							aria-hidden="true"
							className={cn(
								"h-px flex-1 transition-colors duration-300",
								isComplete || isCurrent ? "bg-primary/40" : "bg-border"
							)}
						/>
					)}
					<span
						aria-current={isCurrent ? "step" : undefined}
						className="flex items-center gap-2"
					>
						<span
							className={cn(
								"flex h-5 w-5 shrink-0 items-center justify-center rounded-full font-medium text-[10px] transition-colors duration-200",
								isComplete && "bg-primary text-primary-foreground",
								isCurrent &&
									"bg-primary/10 text-primary ring-1 ring-primary/30",
								!(isComplete || isCurrent) && "bg-muted text-muted-foreground"
							)}
						>
							{isComplete ? (
								<Check className="h-3 w-3" />
							) : (
								<span>{index + 1}</span>
							)}
						</span>
						<span
							className={cn(
								"whitespace-nowrap font-medium text-xs transition-colors duration-200",
								isCurrent ? "text-foreground" : "text-muted-foreground"
							)}
						>
							{label}
						</span>
					</span>
				</li>
			);
		})}
	</ol>
);
